import React, { useState, useEffect, useRef } from 'react';
import type { NodeInspectorProps } from './types';
import { AutoInvestigateCard } from '../workbench/AutoInvestigateCard';
import { AnalyzeGraphCard } from '../workbench/AnalyzeGraphCard';
import { FollowTheMoneySection } from './FollowTheMoneySection';
import { HolisticTraceCard } from '../workbench/HolisticTraceCard';
import { SyndicateCorridorCard } from '../workbench/SyndicateCorridorCard';
import { AnalysisCaseReportsPanel } from './AnalysisCaseReportsPanel';
import { Wand2, BarChart3, ChevronDown, ChevronUp, Coins, Combine, ShieldAlert } from 'lucide-react';
import { usePanelStore, type AnalysisCardFocus } from '../../stores/usePanelStore';
import { toast } from 'sonner';

type AnalysisCard = AnalysisCardFocus | 'syndicate-corridor' | null;

export const AnalysisTab: React.FC<NodeInspectorProps> = (props) => {
    const [expandedCard, setExpandedCard] = useState<AnalysisCard>('auto-investigate');
    const analysisCardFocus = usePanelStore(s => s.analysisCardFocus);
    const analysisCardFocusSeq = usePanelStore(s => s.analysisCardFocusSeq);
    const lastAppliedFocusSeq = useRef(0);

    // Default to Targeted Node Intelligence on node change — skip if a G/I
    // focus request arrived in the same commit (focus effect below wins).
    useEffect(() => {
        if (!props.selectedNode?.id) return;
        const { analysisCardFocus: focus, analysisCardFocusSeq: seq } = usePanelStore.getState();
        if (focus && seq !== lastAppliedFocusSeq.current) return;
        setExpandedCard('auto-investigate');
    }, [props.selectedNode?.id]);

    // Apply G/I (and header) focus — seq ensures repeat presses re-fire
    useEffect(() => {
        if (!analysisCardFocus || analysisCardFocusSeq === 0) return;
        if (analysisCardFocusSeq === lastAppliedFocusSeq.current) return;
        lastAppliedFocusSeq.current = analysisCardFocusSeq;
        setExpandedCard(analysisCardFocus);
    }, [analysisCardFocus, analysisCardFocusSeq]);

    const reportArtifacts = props.savedReportArtifacts || [];

    // The card reports through toasts; NodeInspectorProps.onNotice opens the
    // legal-notice modal instead, so it must not receive the card's messages.
    const corridorNotice = (msg: string, type: 'info' | 'success' | 'warning' | 'error') => {
        if (type === 'error') toast.error(msg);
        else if (type === 'warning') toast.warning(msg);
        else if (type === 'success') toast.success(msg);
        else toast.info(msg);
    };

    return (
        // Natural height stack — the Node Inspector body is the only scroll
        // owner. Previous layout used a full-height outer scroller with a
        // hidden scrollbar plus nested overflow cards (min height traps)
        // which trapped the wheel and hid content below the fold.
        <div className="flex flex-col gap-3 p-3 bg-background/50" data-testid="analysis-tab">
            {/* Case evidence tray — archived investigation reports */}
            <AnalysisCaseReportsPanel
                reports={reportArtifacts}
                onOpenReport={props.onOpenSavedAutoInvestigateReport}
            />

            {/* 1. Node Investigation Algorithm */}
            <div className={`flex flex-col rounded-xl border border-border/80 bg-card shadow-sm relative z-0 overflow-hidden transition-all duration-300 ${expandedCard === 'auto-investigate' ? '' : 'flex-none'}`}>
                <button
                    onClick={() => setExpandedCard(expandedCard === 'auto-investigate' ? null : 'auto-investigate')}
                    className={`flex items-center justify-between w-full p-3 font-semibold text-sm hover:bg-muted/50 transition-colors ${expandedCard === 'auto-investigate' ? 'bg-secondary/30 border-b border-border/50' : ''}`}
                    data-tour="analysis-auto-investigate"
                >
                    <div className="flex items-center gap-2">
                        <Wand2 className="w-4 h-4 text-emerald-500" />
                        Targeted Node Intelligence
                    </div>
                    {expandedCard === 'auto-investigate' ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </button>

                {expandedCard === 'auto-investigate' && (
                    <div className="pt-2 pb-3">
                        <AutoInvestigateCard
                            isDocked
                            isVisible={true}
                            chain={props.chain || props.selectedNode?.chain || null}
                            address={props.selectedNode?.id || null}
                            onAddToGraph={props.onAddElementsToGraph}
                            onNotice={props.onNotice}
                            onWatchlist={props.onWatchlist}
                            dataMode={props.dataMode}
                            cytoscapeInstance={props.cytoscapeInstance}
                            onPersistReport={props.onPersistReport}
                            savedReport={props.savedAutoInvestigateReportFor?.(props.selectedNode?.id || null) || null}
                            onOpenSavedReport={props.onOpenSavedAutoInvestigateReport}
                            onApplyFilterPreset={props.onApplyFilterPreset}
                            caseId={props.caseIdForLoad ?? null}
                            onEnrichElements={props.onEntityEnriched ? (updates) => {
                                updates.forEach(u => props.onEntityEnriched?.(u.id, u.data.entity_name || u.data.label, u.data.entity_type));
                            } : undefined}
                        />
                    </div>
                )}
            </div>

            {/* 2. Follow the Money (per-node, cached across sessions) */}
            <div className="flex flex-col rounded-xl border border-border/80 bg-card shadow-sm relative z-0 overflow-hidden transition-all duration-300 flex-none">
                <button
                    onClick={() => setExpandedCard(expandedCard === 'follow-the-money' ? null : 'follow-the-money')}
                    className={`flex items-center justify-between w-full p-3 font-semibold text-sm hover:bg-muted/50 transition-colors ${expandedCard === 'follow-the-money' ? 'bg-secondary/30 border-b border-border/50' : ''}`}
                    data-tour="analysis-follow-money"
                >
                    <div className="flex items-center gap-2">
                        <Coins className="w-4 h-4 text-amber-500" />
                        Follow the Money
                    </div>
                    {expandedCard === 'follow-the-money' ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </button>

                {expandedCard === 'follow-the-money' && (
                    <FollowTheMoneySection
                        chain={props.chain || props.selectedNode?.chain || null}
                        address={props.selectedNode?.id || null}
                        isRunning={!!props.isFollowMoneyRunning}
                        followMoneyMode={!!props.followMoneyMode}
                        onRun={() => props.onRunFollowMoney?.()}
                        onOpenReport={() => props.onOpenFollowMoneyReport?.()}
                        onToggleDim={(next) => props.onToggleFollowMoneyMode?.(next)}
                    />
                )}
            </div>

            {/* 3. Macro Graph Algorithm */}
            {props.onRunAnalysis && (
                <div className={`flex flex-col rounded-xl border border-border/80 bg-card shadow-sm relative z-0 overflow-hidden transition-all duration-300 ${expandedCard === 'analyze-graph' ? '' : 'flex-none'}`}>
                    <button
                        onClick={() => setExpandedCard(expandedCard === 'analyze-graph' ? null : 'analyze-graph')}
                        className={`flex items-center justify-between w-full p-3 font-semibold text-sm hover:bg-muted/50 transition-colors ${expandedCard === 'analyze-graph' ? 'bg-secondary/30 border-b border-border/50' : ''}`}
                        data-tour="analysis-macro"
                    >
                        <div className="flex items-center gap-2">
                            <BarChart3 className="w-4 h-4 text-primary" />
                            Macro Graph Analysis
                        </div>
                        {expandedCard === 'analyze-graph' ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                    </button>

                    {expandedCard === 'analyze-graph' && (
                        <div className="pt-2 pb-3">
                            <AnalyzeGraphCard
                                isDocked
                                isVisible={true}
                                onRunAnalysis={props.onRunAnalysis}
                                macroAiResults={props.macroAiResults}
                                caseScopedDisabledReason={props.caseScopedDisabledReason ?? null}
                            />
                        </div>
                    )}
                </div>
            )}

            {/* 4. Holistic Multi-Node Trace — graph-level, persists across node selections */}
            <div className="flex flex-col rounded-xl border border-border/80 bg-card shadow-sm relative z-0 overflow-hidden transition-all duration-300 flex-none">
                <button
                    onClick={() => setExpandedCard(expandedCard === 'holistic-trace' ? null : 'holistic-trace')}
                    className={`flex items-center justify-between w-full p-3 font-semibold text-sm hover:bg-muted/50 transition-colors ${expandedCard === 'holistic-trace' ? 'bg-secondary/30 border-b border-border/50' : ''}`}
                    data-tour="analysis-holistic"
                >
                    <div className="flex items-center gap-2">
                        <Combine className="w-4 h-4 text-primary" />
                        Holistic Multi-Node Trace
                    </div>
                    {expandedCard === 'holistic-trace' ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </button>
                {expandedCard === 'holistic-trace' && <HolisticTraceCard onPersistReport={props.onPersistHolisticReport} />}
            </div>

            {/* 5. Shared-funder corridor review + AI playbook draft (node-targeted) */}
            <div className="flex flex-col rounded-xl border border-border/80 bg-card shadow-sm relative z-0 overflow-hidden transition-all duration-300 flex-none">
                <button
                    onClick={() => setExpandedCard(expandedCard === 'syndicate-corridor' ? null : 'syndicate-corridor')}
                    className={`flex items-center justify-between w-full p-3 font-semibold text-sm hover:bg-muted/50 transition-colors ${expandedCard === 'syndicate-corridor' ? 'bg-secondary/30 border-b border-border/50' : ''}`}
                    data-tour="analysis-syndicate-corridor"
                >
                    <div className="flex items-center gap-2">
                        <ShieldAlert className="w-4 h-4 text-rose-500" />
                        Shared-Funder Corridor Review
                    </div>
                    {expandedCard === 'syndicate-corridor' ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                </button>
                {expandedCard === 'syndicate-corridor' && (
                    <SyndicateCorridorCard
                        chain={props.chain || props.selectedNode?.chain || 'TRX'}
                        address={props.selectedNode?.id || null}
                        caseId={props.caseIdForLoad ?? null}
                        onNotice={corridorNotice}
                    />
                )}
            </div>
        </div>
    );
};
